import type { FullItem } from "@/models/FeedContextProvider";
import { formatItemDate, isFirstItemOfDateGroup } from "@/models/feedItemDateGrouping";
import type { Feed, TmsEventRow } from "@/store/types";

/**
 * One row of the rendered feed: a message as the feed builds it, a TMS event chip between messages, or the date
 * header that opens a day.
 *
 * A header is its own row rather than a flag on the row below it so that paging in older history, which can move
 * the start of a day to an older row, never changes the height of a row the virtualizer has already measured.
 */
export type FeedRow =
  | { type: "item"; key: string; at: string; item: FullItem }
  | { type: "tmsEvent"; key: string; at: string; event: TmsEventRow }
  | { type: "dateHeader"; key: string; at: string; dateId: string; label: string };

/**
 * The instants a chip may sit between. `oldest` is the oldest loaded message while older history may exist, so a chip
 * never sits in a hole of unfetched history, and `null` once there is nothing older: no floor.
 */
export type FeedSpan = { oldest: string | null; newest: string | null };

/**
 * The membership whose TMS events a feed shows: the alias feed's driver in a workspace on the Dispatch page, else
 * none. Group and direct-message feeds have no alias member and so never show chips.
 */
export function feedEventMembershipId(
  feed: Pick<Feed, "isAliasFeed" | "aliasMembershipId"> | null | undefined,
  dispatchEnabled: boolean,
): string | null {
  if (!dispatchEnabled || !feed?.isAliasFeed) return null;
  return feed.aliasMembershipId;
}

function insideSpan(occurredAt: string, span: FeedSpan): boolean {
  if (span.oldest !== null && occurredAt < span.oldest) return false;
  return span.newest === null || occurredAt <= span.newest;
}

/** The key of the header row that opens the day `shortDateStr` names. */
export function dateHeaderKey(shortDateStr: string): string {
  return `dateHeader:${shortDateStr}`;
}

/**
 * Merges a feed's items with the events attributed to its driver, ascending by instant (item.createdAt /
 * event.occurredAt), keeping only events inside `span` so a chip never appears in a hole the items have not been
 * fetched for; a caller with no floor to give yet passes no events. A date header row opens each day of the merged
 * sequence, whether a message or a chip is first in it. Ties put the item first. Items are passed through unchanged.
 */
export function interleaveFeedRows(
  items: readonly FullItem[],
  events: readonly TmsEventRow[],
  span: FeedSpan,
): FeedRow[] {
  const shown = events
    .filter((event) => insideSpan(event.occurredAt, span))
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt) || a.id.localeCompare(b.id));

  const rows: FeedRow[] = [];
  let previousAt: string | null = null;
  // Every row goes through here so the day boundary is read off the merged sequence, not the items alone.
  const push = (row: Exclude<FeedRow, { type: "dateHeader" }>) => {
    const { shortDateStr, formattedDate } = formatItemDate(row.at);
    if (isFirstItemOfDateGroup(shortDateStr, previousAt)) {
      rows.push({
        type: "dateHeader",
        key: dateHeaderKey(shortDateStr),
        at: row.at,
        dateId: shortDateStr,
        label: formattedDate,
      });
    }
    rows.push(row);
    previousAt = row.at;
  };
  const pushEvent = (event: TmsEventRow) => {
    push({ type: "tmsEvent", key: `tmsEvent:${event.id}`, at: event.occurredAt, event });
  };

  let nextEvent = 0;
  for (const item of items) {
    // Chips that happened before this message go first, then the message itself.
    while (nextEvent < shown.length && (shown[nextEvent]?.occurredAt ?? "") < item.createdAt) {
      pushEvent(shown[nextEvent] as TmsEventRow);
      nextEvent += 1;
    }
    push({ type: "item", key: `item:${item.id}`, at: item.createdAt, item });
  }
  // Chips newer than the last message trail it.
  for (const event of shown.slice(nextEvent)) pushEvent(event);
  return rows;
}

/**
 * Maps between the list's row indices and the items' own indices, so the effects that page, download content and
 * scroll by item keep their item arithmetic while the list renders rows. A chip or header row maps to the item that
 * follows it, so a visible range that starts on one still begins at the first message on screen.
 */
export function feedRowIndexes(rows: readonly FeedRow[]): { rowIndexOfItem: number[]; itemIndexOfRow: number[] } {
  const rowIndexOfItem: number[] = [];
  const itemIndexOfRow: number[] = [];
  for (const [rowIndex, row] of rows.entries()) {
    itemIndexOfRow.push(rowIndexOfItem.length);
    if (row.type === "item") rowIndexOfItem.push(rowIndex);
  }
  // A trailing chip has no item after it; clamp to the last item so a range ending on it stays in bounds.
  const lastItem = Math.max(0, rowIndexOfItem.length - 1);
  return { rowIndexOfItem, itemIndexOfRow: itemIndexOfRow.map((itemIndex) => Math.min(itemIndex, lastItem)) };
}
